import React from "react";

import { ArticleEntityLinkEntity } from "@/mk2/apps/wiki/schemas";
import { Img } from "@/mk2/components/Img";
import {
    WIKI_ARTICLE_REVIEWED_BY,
    WIKI_ARTICLE_REVIEWED_BY_FEMALE,
    WIKI_ARTICLE_REVIEWED_BY_MALE,
} from "@/mk/autogenerated/translation";
import { photoUrl } from "@/mk/photo/photoUrl";

import styles from "./ReviewerAttribution.mscss";

interface Props {
    entityLinks?: ArticleEntityLinkEntity[];
}

// Maps a sameAs URL to a short prefix shown before the link (e.g. "FB", "IG").
// Extend this list to label more social networks; unknown URLs fall back to the
// bare domain (without a leading "www.").
const SOCIAL_LABELS: { match: string; label: string }[] = [
    { match: "facebook.com", label: "FB" },
    { match: "fb.com", label: "FB" },
    { match: "instagram.com", label: "IG" },
    { match: "youtube.com", label: "YouTube" },
    { match: "youtu.be", label: "YouTube" },
    { match: "linkedin.com", label: "LinkedIn" },
    { match: "tiktok.com", label: "TikTok" },
    { match: "twitter.com", label: "X" },
    { match: "x.com", label: "X" },
];

// Editors paste URLs into the adminclub sameAs field by hand, so a scheme is not
// guaranteed. Prepend https:// when missing, otherwise the anchor href would be
// treated as a relative path.
function normalizeUrl(url: string): string {
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function socialLabel(url: string): string {
    try {
        const host = new URL(url).hostname.replace(/^www\./, "");
        const known = SOCIAL_LABELS.find((s) => host === s.match || host.endsWith(`.${s.match}`));
        return known ? known.label : host;
    } catch {
        return "WEB";
    }
}

function reviewedByLabel(isFemale?: boolean | null): string {
    if (isFemale === true) return WIKI_ARTICLE_REVIEWED_BY_FEMALE;
    if (isFemale === false) return WIKI_ARTICLE_REVIEWED_BY_MALE;
    return WIKI_ARTICLE_REVIEWED_BY;
}

// Renders the "Text po odbornej stránke skontroloval(a): …" block at the end of a
// wiki article. Single source of truth: the reviewer WikiEntity(person) links
// (role="reviewer"), the same relation that feeds the JSON-LD `reviewedBy` node.
export const ReviewerAttribution: React.FC<Props> = ({ entityLinks }) => {
    const reviewers = (entityLinks ?? []).filter((link) => link.role === "reviewer");
    if (!reviewers.length) return null;

    return (
        <div className={styles.ReviewerAttribution}>
            {reviewers.map((link) => (
                <div key={link.id} className={styles.ReviewerAttribution__item}>
                    <div className={styles.ReviewerAttribution__label}>
                        {reviewedByLabel(link.entity.isFemale)}
                    </div>
                    <div className={styles.ReviewerAttribution__reviewer}>
                        {link.entity.photo && (
                            <Img
                                src={photoUrl(link.entity.photo, "s150x150")}
                                className={styles.ReviewerAttribution__photo}
                                width={56}
                                height={56}
                                alt={link.entity.name}
                            />
                        )}
                        <div className={styles.ReviewerAttribution__info}>
                            <div className={styles.ReviewerAttribution__name}>
                                {link.entity.name}
                            </div>
                            {link.entity.jobTitle && (
                                <div className={styles.ReviewerAttribution__detail}>
                                    {link.entity.jobTitle}
                                </div>
                            )}
                            {link.entity.affiliation && (
                                <div className={styles.ReviewerAttribution__detail}>
                                    {link.entity.affiliationUrl ? (
                                        <a
                                            href={link.entity.affiliationUrl}
                                            className={styles.ReviewerAttribution__link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {link.entity.affiliation}
                                        </a>
                                    ) : (
                                        link.entity.affiliation
                                    )}
                                </div>
                            )}
                            {(link.entity.sameAsUrls ?? []).map((url) => {
                                const href = normalizeUrl(url);
                                return (
                                    <div key={url} className={styles.ReviewerAttribution__social}>
                                        {`${socialLabel(href)}: `}
                                        <a
                                            href={href}
                                            className={styles.ReviewerAttribution__link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {url}
                                        </a>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
};
